import type {
  IframePoolItem,
  IframePoolOfferOdds,
  JustPulledItem,
  PoolOfferDetail,
} from '../../../modules/api';
import { CategoryPillRow, type CategoryPillItem } from '../../../modules/design-system';
import { HelpLink } from '../../../modules/help';
import { FairnessTicketKeeper, ProvablyFairSection } from '../../../modules/fairness/ui';
import { OddsPanel, OddsPanelSkeleton } from '../../../modules/odds';
import { PotentialHits } from '../../../modules/potentialHits';
import { JustPulled } from '../../../modules/justPulled';
import { BottomAction, MOBILE_RAIL_SPACER_HEIGHT } from './BottomAction';
import { HeroSection, HeroSkeleton } from './HeroSection';
import { isOfferAvailable, statusLabel } from './offerStatus';
import { PackInfoCard, PackInfoCardSkeleton } from './PackInfoCard';

/**
 * Grid layout for the pack-detail page.
 *
 * Breakpoints (container-relative, not viewport):
 *   < 900px  → single column, `action` block appears at the end of the flow
 *   ≥ 900px  → two columns (hero | 360px sidebar); `action` moves into the
 *              sidebar.
 */

export interface PackLayoutProps {
  offer: PoolOfferDetail | null;
  odds: IframePoolOfferOdds | null;
  /** Backend `max_for_purchase_count`; `undefined` = still loading. */
  maxForPurchaseCount: number | undefined;
  potentialHits: IframePoolItem[];
  potentialHitsLoading: boolean;
  /** Recent global pulls for the live "Just Pulled" strip. */
  justPulledItems: JustPulledItem[];
  justPulledLoading: boolean;
  /** Click on the desktop chevron — opens the in-iframe ?view=just-pulled page. */
  onShowJustPulled?: () => void;
  categories: CategoryPillItem[];
  activeCategoryId?: string;
  onCategorySelect: (id: string) => void;
  onTierSelect?: (poolOfferId: number) => void;
  /** Pack quantity + buy wiring. Optional so PackPage can supply lazily. */
  quantity?: number;
  onQuantityChange?: (next: number) => void;
  onBuy?: () => void;
  unitPrice?: number | string;
}

export function PackLayout({
  offer,
  odds,
  maxForPurchaseCount,
  potentialHits,
  potentialHitsLoading,
  justPulledItems,
  justPulledLoading,
  onShowJustPulled,
  categories,
  activeCategoryId,
  onCategorySelect,
  onTierSelect,
  quantity = 1,
  onQuantityChange,
  onBuy,
  unitPrice = 0,
}: PackLayoutProps) {
  // Not purchasable (e.g. restocking): the action bar stays, locked, and
  // reads the status instead of the price.
  const unavailableLabel =
    offer && !isOfferAvailable(offer.status) ? statusLabel(offer.status) : undefined;

  return (
    <div className="@container px-4 pb-8 @[600px]:px-6 @[900px]:px-8">
      <div
        className="
          mx-auto max-w-350
          grid grid-cols-1 gap-2
          @[900px]:grid-cols-[minmax(0,1fr)_360px]
          @[900px]:gap-6
          @[1200px]:grid-cols-[minmax(0,1fr)_400px]
        "
      >
        {/* HERO — left, spans 1 row on desktop */}
        <div
          className="
            relative
            min-h-75
            @[600px]:min-h-90
            @[900px]:min-h-110
            @[1200px]:min-h-125
            @[900px]:col-start-1 @[900px]:row-start-1
          "
        >
          {offer ? (
            <HeroSection
              packImageUrl={offer.packImageUrl}
              title={offer.title}
              heroAnimationUrl={offer.heroAnimationUrl}
              status={offer.status}
            />
          ) : (
            <HeroSkeleton />
          )}
        </div>

        {/* MOBILE-ONLY sport pills row under the hero (< 600px) —
            Header hides its own pill row below 600px, this takes over. */}
        <div className="py-2 @[600px]:hidden">
          <CategoryPillRow
            categories={categories}
            {...(activeCategoryId !== undefined ? { activeId: activeCategoryId } : {})}
            onSelect={onCategorySelect}
            centered={false}
          />
        </div>

        {/* SIDEBAR — right, flex-col with info + odds + (action on desktop) */}
        <aside
          className="
            flex flex-col gap-2
            @[900px]:col-start-2 @[900px]:row-start-1
          "
        >
          {offer ? (
            <PackInfoCard
              title={offer.title}
              tiers={offer.suitableBadges}
              description={offer.description}
              buybackPercent={offer.buybackPercent}
              {...(onTierSelect ? { onTierSelect } : {})}
            />
          ) : (
            <PackInfoCardSkeleton />
          )}

          {/* Classic has no odds dialog: the provably-fair row sits inside the card. */}
          {odds ? (
            <OddsPanel data={odds} footer={<ProvablyFairSection offerId={offer?.id} skin="classic" />} />
          ) : (
            <OddsPanelSkeleton />
          )}
          <FairnessTicketKeeper offerId={offer?.id} />

          {/* Desktop-only action block inside sidebar. */}
          <div className="hidden @[900px]:block">
            <BottomAction
              quantity={quantity}
              onQuantityChange={onQuantityChange ?? (() => {})}
              onBuy={onBuy ?? (() => {})}
              unitPrice={unitPrice}
              {...(maxForPurchaseCount !== undefined ? { maxQty: maxForPurchaseCount } : {})}
              {...(unavailableLabel ? { unavailableLabel } : {})}
            />
          </div>

          {/* Help — desktop only, directly under the RIP rail. `contents`
              rather than `block` so the anchor is the aside's own flex item
              and, while the operator keeps the knob off, takes no `gap-2`
              step of its own. */}
          {/* `mr-3` = the RIP disc's own `right-[12px]` inside the rail, so
              the two right edges line up exactly. */}
          <div className="hidden @[900px]:contents">
            <HelpLink className="self-end mr-3" />
          </div>
        </aside>

        {/* DESKTOP-ONLY Just Pulled — left column below hero, before
            Potential Hits. Hidden on mobile (mobile variant rendered
            above the grid). */}
        <div className="hidden @[900px]:block @[900px]:col-start-1 @[900px]:row-start-2">
          <JustPulled
            items={justPulledItems}
            loading={justPulledLoading}
            {...(onShowJustPulled ? { onShowAll: onShowJustPulled } : {})}
          />
        </div>

        {/* Potential Hits — left column below hero (mobile) / below Just
            Pulled (desktop). */}
        <div className="@[900px]:col-start-1 @[900px]:row-start-3">
          <PotentialHits
            items={potentialHits}
            loading={potentialHitsLoading}
          />
        </div>

        {/* Help — mobile only: the end of the scroll, under Potential Hits.
            Desktop renders it in the sidebar under the RIP rail instead. */}
        <div className="contents @[900px]:hidden">
          <HelpLink className="justify-self-center" />
        </div>

        {/* Mobile spacer — reserves 160px under the in-flow content so it
            never slides under the portal'd `MobileBar` (which lives in
            `<body>` and is fixed to the viewport bottom). The bar itself
            is rendered exactly once from the desktop `<BottomAction>`
            mount above — its `MobileBar` child portals to `<body>`
            globally, so a second mount here would create a duplicate
            rail. */}
        <div
          className="@[900px]:hidden"
          style={{ height: MOBILE_RAIL_SPACER_HEIGHT }}
          aria-hidden
        />

      </div>
    </div>
  );
}
